import { useState } from 'react';
import type { LanguageMode } from '../types';
import { useSettings } from '../state/SettingsContext';

/**
 * First run: choose the display language, then the learning style.
 * Both texts are shown in both languages here, because at this point we do not
 * yet know which one the user reads.
 */
export function OnboardingScreen() {
  const { settings, update } = useSettings();
  const [step, setStep] = useState<1 | 2>(1);
  const [language, setLanguage] = useState<LanguageMode>(settings.languageMode);
  const [easy, setEasy] = useState<boolean>(true);

  const finish = () => {
    update({ languageMode: language, easyMode: easy, onboardingComplete: true });
  };

  return (
    <main id="main" className="app-main stack" style={{ paddingTop: 'var(--space-6)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', lineHeight: 1 }} aria-hidden="true">
          🚗
        </div>
        <h1 style={{ fontSize: '1.8rem', marginBottom: 0 }}>رخصة أوهايو بالعربي</h1>
        <p className="ltr" style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
          Ohio Permit Arabic
        </p>
      </div>

      {step === 1 ? (
        <section className="card stack" aria-labelledby="onboard-lang">
          <h2 id="onboard-lang" style={{ margin: 0 }}>
            اختر اللغة
            <span className="app-header__sub ltr">Choose Language</span>
          </h2>

          <div className="tile-grid">
            <button
              type="button"
              className={`tile${language === 'ar' ? ' tile--primary' : ''}`}
              aria-pressed={language === 'ar'}
              onClick={() => setLanguage('ar')}
            >
              <span className="tile__icon" aria-hidden="true">
                ع
              </span>
              <span className="tile__label">
                العربية
                <span className="tile__sub ltr">Arabic only</span>
              </span>
            </button>

            <button
              type="button"
              className={`tile${language === 'ar-en' ? ' tile--primary' : ''}`}
              aria-pressed={language === 'ar-en'}
              onClick={() => setLanguage('ar-en')}
            >
              <span className="tile__icon" aria-hidden="true">
                ع/A
              </span>
              <span className="tile__label">
                العربية مع الإنجليزية
                <span className="tile__sub ltr">Arabic + English</span>
              </span>
            </button>

            <button
              type="button"
              className={`tile${language === 'en' ? ' tile--primary' : ''}`}
              aria-pressed={language === 'en'}
              onClick={() => setLanguage('en')}
            >
              <span className="tile__icon" aria-hidden="true">
                A
              </span>
              <span className="tile__label ltr">
                English
                <span className="tile__sub">الإنجليزية فقط</span>
              </span>
            </button>
          </div>

          <button type="button" className="btn btn--primary btn--big btn--block" onClick={() => setStep(2)}>
            التالي · Next
          </button>
        </section>
      ) : (
        <section className="card stack" aria-labelledby="onboard-style">
          <h2 id="onboard-style" style={{ margin: 0 }}>
            اختر طريقة التعلّم
            <span className="app-header__sub ltr">Choose Learning Style</span>
          </h2>

          <div className="tile-grid">
            <button
              type="button"
              className={`tile${easy ? ' tile--primary' : ''}`}
              aria-pressed={easy}
              onClick={() => setEasy(true)}
            >
              <span className="tile__icon" aria-hidden="true">
                🌟
              </span>
              <span className="tile__label">
                وضع التعلّم السهل
                <span className="tile__sub">سؤال واحد، خط كبير، أزرار كبيرة — مُوصى به</span>
                <span className="tile__sub ltr">Easy Learning Mode — recommended</span>
              </span>
            </button>

            <button
              type="button"
              className={`tile${!easy ? ' tile--primary' : ''}`}
              aria-pressed={!easy}
              onClick={() => setEasy(false)}
            >
              <span className="tile__icon" aria-hidden="true">
                ⚙️
              </span>
              <span className="tile__label">
                الوضع العادي
                <span className="tile__sub">كل الميزات وخيارات أكثر</span>
                <span className="tile__sub ltr">Standard Mode</span>
              </span>
            </button>
          </div>

          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
            يمكنك تغيير هذا لاحقاً من الإعدادات.
            <span className="ltr" style={{ display: 'block' }}>
              You can change this later in Settings.
            </span>
          </p>

          <div className="row">
            <button type="button" className="btn" onClick={() => setStep(1)}>
              رجوع · Back
            </button>
            <div className="spacer" />
            <button type="button" className="btn btn--primary btn--big" onClick={finish}>
              ابدأ · Start
            </button>
          </div>
        </section>
      )}
    </main>
  );
}
